import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle, ArrowRight, CreditCard, FileText, Info, MapPin, Pencil,
  Router as RouterIcon, Wifi, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { LocationPicker } from "@/components/LocationPicker";
import type { ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import {
  applyPydanticErrors, flattenRhfErrors, labelForPath,
} from "@/lib/formErrors";
import {
  contratoUpdateFieldsSchema,
  cpeVendorEnum, CPE_VENDOR_LABEL,
  polarizacionEnum,
  onuVendorEnum, ONU_VENDOR_LABEL,
  splitterNivelEnum,
  type ContratoDetail,
  type ContratoUpdateFieldsInput,
  type ContratoUpdateInput,
} from "./schemas";
import { useEditarUnificado } from "./api";
import { ReemplazarOnuDialog } from "./ReemplazarOnuDialog";
import { MoverOnuDialog } from "./MoverOnuDialog";
import { useJobPolling } from "@/features/jobs/useJobPolling";
import { useListPlanes } from "@/features/planes/api";
import { useListRouters } from "@/features/routers/api";
import { useListRedes, useIpsDisponibles } from "@/features/redes/api";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  contrato: ContratoDetail;
  onSaved?: () => void;
};

/**
 * rc107 — Editor UNIFICADO de contrato (diseño _design/contrato-edit-unificado-v1).
 *
 * Layout: rail lateral de secciones · contenido · panel "Resumen de cambios"
 * con antes → después en vivo, tags ROUTER y motivo inline. Un solo guardado
 * llama POST /editar-unificado que orquesta update + mover + cambiar-ip con
 * el MÍNIMO de jobs al router (un solo reconcile con el estado final).
 *
 * El switch wireless↔fibra queda para la integración con ByteOLT.
 */

type SectionId = "servicio" | "conexion" | "facturacion" | "ubicacion" | "notas";

const SECTIONS: { id: SectionId; icon: typeof Wifi; title: string; desc: string }[] = [
  { id: "servicio", icon: Wifi, title: "Servicio", desc: "Plan, precio y equipo" },
  { id: "conexion", icon: RouterIcon, title: "Conexión", desc: "PPPoE, router, segmento e IP" },
  { id: "facturacion", icon: CreditCard, title: "Facturación", desc: "Corte, tolerancia y exentos" },
  { id: "ubicacion", icon: MapPin, title: "Ubicación", desc: "GPS de la instalación" },
  { id: "notas", icon: FileText, title: "Notas y banderas", desc: "Notas, exclusiones" },
];

/** Un cambio detectado para el panel resumen. */
type Cambio = {
  key: string;
  label: string;
  before: string;
  after: string;
  router: boolean;
  pane: SectionId;
};

type FieldDef = {
  path: string;
  label: string;
  pane: SectionId;
  router?: boolean;
  /** Formatea el valor para mostrar (default: string tal cual, vacío = "—"). */
  fmt?: (v: unknown) => string;
};

const fmtBool = (v: unknown) => (v ? "sí" : "no");

const FIELD_DEFS: FieldDef[] = [
  { path: "plan_id", label: "Plan", pane: "servicio" },
  { path: "precio_override", label: "Precio override", pane: "servicio" },
  { path: "equipo_modalidad", label: "Modalidad equipo", pane: "servicio" },
  { path: "equipo_descripcion", label: "Descripción equipo", pane: "servicio" },
  { path: "equipo_costo_instalacion", label: "Costo instalación", pane: "servicio" },
  // Wireless — MAC dispara re-aprovisionamiento
  { path: "wireless_detalle.cpe_vendor", label: "Vendor CPE", pane: "servicio" },
  { path: "wireless_detalle.cpe_mac", label: "MAC CPE", pane: "servicio", router: true },
  { path: "wireless_detalle.cpe_modelo", label: "Modelo CPE", pane: "servicio" },
  { path: "wireless_detalle.cpe_serial", label: "Serial CPE", pane: "servicio" },
  { path: "wireless_detalle.cpe_ip_management", label: "IP mgmt CPE", pane: "servicio" },
  { path: "wireless_detalle.polarizacion", label: "Polarización", pane: "servicio" },
  { path: "wireless_detalle.ssid_conexion", label: "SSID", pane: "servicio" },
  { path: "wireless_detalle.frecuencia_mhz", label: "Frecuencia MHz", pane: "servicio" },
  { path: "wireless_detalle.cpe_usuario_admin", label: "User admin CPE", pane: "servicio" },
  { path: "wireless_detalle.cpe_password_admin", label: "Pass admin CPE", pane: "servicio" },
  // Fibra
  { path: "fiber_detalle.onu_vendor", label: "Vendor ONU", pane: "servicio" },
  { path: "fiber_detalle.onu_mac", label: "MAC ONU", pane: "servicio", router: true },
  { path: "fiber_detalle.onu_serial", label: "Serial ONU", pane: "servicio" },
  { path: "fiber_detalle.onu_modelo", label: "Modelo ONU", pane: "servicio" },
  { path: "fiber_detalle.onu_ip_management", label: "IP mgmt ONU", pane: "servicio" },
  { path: "fiber_detalle.olt_puerto", label: "Puerto OLT", pane: "servicio" },
  { path: "fiber_detalle.splitter_nivel", label: "Splitter", pane: "servicio" },
  // Facturación
  { path: "fecha_corte", label: "Día de corte", pane: "facturacion", fmt: (v) => `Día ${v}` },
  { path: "descuento_pct", label: "Descuento %", pane: "facturacion" },
  { path: "dias_gracia_override", label: "Días de gracia", pane: "facturacion" },
  { path: "max_facturas_vencidas_permitidas", label: "Tolerancia vencidas", pane: "facturacion" },
  { path: "exento_facturacion", label: "Exento facturación", pane: "facturacion", fmt: fmtBool },
  { path: "exento_monitoreo", label: "Exento monitoreo", pane: "facturacion", fmt: fmtBool },
  { path: "facturar_suspendido", label: "Facturar suspendido", pane: "facturacion", fmt: fmtBool },
  // Ubicación (lat+lng se combinan como "GPS" aparte)
  { path: "ubicacion_referencia", label: "Referencia ubicación", pane: "ubicacion" },
  // Notas y banderas
  { path: "notas", label: "Notas", pane: "notas" },
  { path: "notas_internas", label: "Notas internas", pane: "notas" },
  { path: "excluir_reintento_auto", label: "Excluir reintentos", pane: "notas", fmt: fmtBool },
];

function getPath(obj: unknown, path: string): unknown {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return path.split(".").reduce<any>((o, k) => (o == null ? undefined : o[k]), obj);
}

/** Normaliza para comparar: null/undefined/"" son equivalentes; bool → "1"/"0". */
function norm(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "1" : "0";
  return String(v).trim();
}

const showVal = (v: string) => (v === "" ? "—" : v);

export function ContratoEditDialog({ open, onOpenChange, contrato, onSaved }: Props) {
  const [section, setSection] = useState<SectionId>("servicio");
  const [error, setError] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);
  // rc117 — Reemplazo de ONU (equipo quemado): modal dedicado
  const [showReemplazoOnu, setShowReemplazoOnu] = useState(false);
  // rc118 — Mover de domicilio: la ONU cambia de PON/OLT (misma serie)
  const [showMoverOnu, setShowMoverOnu] = useState(false);

  const hasWireless = !!contrato.wireless_detalle;
  const hasFiber = !!contrato.fiber_detalle;
  const ipActual = (contrato.ip_asignada ?? "").split("/")[0];

  // v1.5.0-rc16: solo admin/superadmin puede cambiar plan
  const userRol = useAuthStore((s) => s.user?.rol?.nombre);
  const canChangePlan = userRol === "administrador" || userRol === "superadmin";
  const { data: planesData } = useListPlanes({ page: 1, size: 200 });
  // rc145 — SOLO planes de la misma tecnología: un plan de la otra tecnología
  // no es "cambiar plan", es migrar (el backend también lo rechaza con
  // plan_otra_tecnologia). El plan actual siempre se lista aunque esté inactivo.
  const tipoActual = hasFiber ? "fiber" : "wireless";
  const planes = canChangePlan
    ? (planesData?.items ?? []).filter(
        (p) => p.tipo_servicio === tipoActual && (p.activo || p.id === contrato.plan_id),
      )
    : [];

  // rc107 — router / segmento / IP (fuera del schema zod: van aparte al endpoint)
  const [routerId, setRouterId] = useState(contrato.router_id);
  const [redId, setRedId] = useState(contrato.red_id);
  const [ipSel, setIpSel] = useState("");

  const { data: routersData } = useListRouters({ size: 200 });
  const { data: redesData } = useListRedes({ page: 1, size: 200 });
  const routers = routersData?.items ?? [];
  const routerActual = routers.find((r) => r.id === contrato.router_id);
  const routerDest = routers.find((r) => r.id === routerId);

  // Segmentos usables en el router elegido: atados a él + globales. Sin uso interno.
  const redes = useMemo(
    () =>
      (redesData?.items ?? []).filter(
        (rd) => (!rd.router_id || rd.router_id === routerId) && !rd.uso_interno && rd.activo,
      ),
    [redesData, routerId],
  );
  const redActualObj = (redesData?.items ?? []).find((r) => r.id === contrato.red_id);
  const redDestObj = (redesData?.items ?? []).find((r) => r.id === redId);

  const isMoving = routerId !== contrato.router_id || redId !== contrato.red_id;
  // IPs libres del segmento ACTUAL — solo aplican si NO hay traslado
  const { data: ipsData, isLoading: ipsLoading } = useIpsDisponibles(
    open && !isMoving ? contrato.red_id : null,
  );
  const ipsLibres = ipsData?.ips ?? [];
  const ipChanged = !isMoving && !!ipSel && ipSel !== ipActual;

  const {
    register, handleSubmit, reset, control, setError: setFieldError, setValue,
    formState: { errors, isSubmitted, isValid },
  } = useForm<ContratoUpdateFieldsInput>({
    resolver: zodResolver(contratoUpdateFieldsSchema),
    defaultValues: buildDefaults(contrato),
  });

  const watched = useWatch({ control });
  const defaults = useMemo(() => buildDefaults(contrato), [contrato]);

  // Checkboxes para overrides de precio/gracia (patrón dev61)
  const [useOverridePrecio, setUseOverridePrecio] = useState(false);
  const [useOverrideGracia, setUseOverrideGracia] = useState(false);

  function togglePrecioOverride(on: boolean) {
    setUseOverridePrecio(on);
    if (!on) setValue("precio_override", "", { shouldValidate: false });
  }
  function toggleGraciaOverride(on: boolean) {
    setUseOverrideGracia(on);
    if (!on) setValue("dias_gracia_override", "", { shouldValidate: false });
  }

  useEffect(() => {
    if (open) {
      reset(buildDefaults(contrato));
      setSection("servicio");
      setError(null);
      setMotivo("");
      setRouterId(contrato.router_id);
      setRedId(contrato.red_id);
      setIpSel("");
      setUseOverridePrecio(
        contrato.precio_override !== null && contrato.precio_override !== undefined &&
        Number(contrato.precio_override) > 0,
      );
      setUseOverrideGracia(
        contrato.dias_gracia_override !== null && contrato.dias_gracia_override !== undefined,
      );
    }
  }, [open, contrato, reset]);

  function onRouterChange(id: string) {
    setRouterId(id);
    setRedId(id === contrato.router_id ? contrato.red_id : "");
    setIpSel("");
  }

  const editarMut = useEditarUnificado(contrato.id);

  useJobPolling(pollingJobId, {
    invalidate: [["contratos"], ["clientes", "resumen"]],
    onComplete: () => {
      setPollingJobId(null);
      toast.success("Cambios aplicados al router");
    },
    onError: (job) => {
      setPollingJobId(null);
      toast.error("Cambios guardados pero router falló", {
        description: job.error ?? "Revisa /jobs",
      });
    },
  });

  // ── Motor de cambios: diff en vivo contra los defaults ──────────────────
  const cambios = useMemo<Cambio[]>(() => {
    const out: Cambio[] = [];
    for (const def of FIELD_DEFS) {
      if (def.path.startsWith("wireless_detalle.") && !hasWireless) continue;
      if (def.path.startsWith("fiber_detalle.") && !hasFiber) continue;
      if (def.path === "plan_id" && !canChangePlan) continue;
      const cur = getPath(watched, def.path);
      const was = getPath(defaults, def.path);
      if (norm(cur) === norm(was)) continue;
      const fmt = def.fmt ?? ((v: unknown) => norm(v));
      let before = norm(was) === "" ? "—" : String(fmt(was));
      let after = norm(cur) === "" ? "—" : String(fmt(cur));
      if (def.path === "plan_id") {
        const pB = planes.find((p) => p.id === was)?.nombre;
        const pA = planes.find((p) => p.id === cur)?.nombre;
        before = pB ?? before; after = pA ?? after;
      }
      out.push({ key: def.path, label: def.label, before, after, router: !!def.router, pane: def.pane });
    }
    // PPPoE: default "" = sin cambio; algo escrito distinto al actual = cambio
    const pu = norm(getPath(watched, "pppoe_user"));
    if (pu !== "" && pu !== contrato.pppoe_user) {
      out.push({ key: "pppoe_user", label: "Usuario PPPoE", before: contrato.pppoe_user, after: pu, router: true, pane: "conexion" });
    }
    const pp = norm(getPath(watched, "pppoe_password"));
    if (pp !== "") {
      out.push({ key: "pppoe_password", label: "Password PPPoE", before: "••••••••", after: "nueva", router: true, pane: "conexion" });
    }
    // GPS: lat+lng como un solo cambio
    const latC = norm(getPath(watched, "latitud")); const lngC = norm(getPath(watched, "longitud"));
    const latW = norm(getPath(defaults, "latitud")); const lngW = norm(getPath(defaults, "longitud"));
    if (latC !== latW || lngC !== lngW) {
      const f = (la: string, ln: string) =>
        la === "" ? "—" : `${Number(la).toFixed(6)}, ${Number(ln || "0").toFixed(6)}`;
      out.push({ key: "gps", label: "GPS", before: f(latW, lngW), after: f(latC, lngC), router: false, pane: "ubicacion" });
    }
    // Traslado de router/segmento (síntesis, no viene del form)
    if (isMoving) {
      const f = (rn?: string, cidr?: string) => [rn ?? "?", cidr ?? ""].filter(Boolean).join(" · ");
      out.push({
        key: "mover", label: "Router / segmento", router: true, pane: "conexion",
        before: f(routerActual?.nombre, redActualObj?.cidr),
        after: redId ? f(routerDest?.nombre, redDestObj?.cidr) : `${routerDest?.nombre ?? "?"} · elige segmento…`,
      });
    } else if (ipChanged) {
      out.push({ key: "ip", label: "IP asignada", before: ipActual, after: ipSel, router: true, pane: "conexion" });
    }
    return out;
  }, [watched, defaults, hasWireless, hasFiber, canChangePlan, planes, contrato.pppoe_user,
      isMoving, ipChanged, ipSel, ipActual, routerActual, routerDest, redActualObj, redDestObj, redId]);

  const routerN = cambios.filter((c) => c.router).length;
  const panesChanged = useMemo(() => new Set(cambios.map((c) => c.pane)), [cambios]);
  const canSave =
    cambios.length > 0 && motivo.trim().length >= 5 &&
    (!isMoving || !!redId) && !editarMut.isPending;

  // ── Submit unificado ─────────────────────────────────────────────────────
  const onValidSubmit: SubmitHandler<ContratoUpdateFieldsInput> = async (values) => {
    setError(null);
    if (isMoving && !redId) {
      setSection("conexion");
      setError("Para mover de router elige también el segmento destino.");
      return;
    }
    try {
      // Overrides desmarcados con valor previo → null explícito (fix dev61);
      // vaciar latitud borra la ubicación completa (rc101).
      const overridePatches: Record<string, null> = {};
      const teniaPrecio =
        contrato.precio_override !== null && contrato.precio_override !== undefined &&
        Number(contrato.precio_override) > 0;
      if (!useOverridePrecio && teniaPrecio) overridePatches.precio_override = null;
      const teniaGracia =
        contrato.dias_gracia_override !== null && contrato.dias_gracia_override !== undefined;
      if (!useOverrideGracia && teniaGracia) overridePatches.dias_gracia_override = null;
      const teniaGps = contrato.latitud !== null && contrato.latitud !== undefined;
      const latVacia = String(values.latitud ?? "").trim() === "";
      if (teniaGps && latVacia) {
        overridePatches.latitud = null;
        overridePatches.longitud = null;
        overridePatches.ubicacion_referencia = null;
      }
      const update = {
        ...(clean({ ...values, motivo: motivo.trim() }) as ContratoUpdateInput),
        ...overridePatches,
      } as ContratoUpdateInput;

      const res = await editarMut.mutateAsync({
        update,
        nuevo_router_id: isMoving ? routerId : null,
        nueva_red_id: isMoving ? redId : null,
        nueva_ip: ipChanged ? ipSel : null,
      });
      onSaved?.();
      onOpenChange(false);
      const lastJob = res.job_ids[res.job_ids.length - 1] ?? null;
      if (lastJob) {
        setPollingJobId(lastJob);
        toast.info(
          isMoving ? "Guardado · trasladando al router destino" : "Guardado · aplicándose al router",
          {
            description: res.job_ids.length > 1
              ? `${res.job_ids.length} jobs encolados (retiro + alta con el estado final).`
              : "Un solo reconcile con todos los cambios.",
          },
        );
      } else {
        toast.success("Cambios guardados", {
          description: "Sin cambios que requieran tocar el router.",
        });
      }
    } catch (e) {
      const api = e as ApiError;
      const affected = applyPydanticErrors<ContratoUpdateFieldsInput>(api, setFieldError);
      if (affected.length > 0) {
        setError(`Revisa los ${affected.length} campo(s) con error.`);
      } else {
        setError(api.message ?? "No se pudo guardar");
      }
    }
  };

  // Ubicación → LocationPicker
  const latNum = Number(String(watched?.latitud ?? "").trim());
  const lngNum = Number(String(watched?.longitud ?? "").trim());
  const hasGps =
    String(watched?.latitud ?? "").trim() !== "" &&
    String(watched?.longitud ?? "").trim() !== "" &&
    Number.isFinite(latNum) && Number.isFinite(lngNum);

  const estatusColor: Record<string, string> = {
    activo: "bg-emerald-500/10 text-emerald-500",
    suspendido: "bg-amber-500/10 text-amber-500",
    error: "bg-red-500/10 text-red-500",
    pendiente: "bg-blue-500/10 text-blue-400",
    cancelado: "bg-zinc-500/10 text-zinc-400",
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange} size="3xl" closeOnBackdrop={false}>
      <form onSubmit={handleSubmit(onValidSubmit)}>
        {/* ── Header ── */}
        <div className="flex items-center gap-3 border-b bg-muted/30 px-5 py-3.5 pr-12">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Pencil size={15} />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold leading-tight">Editar contrato</h2>
            <p className="truncate font-mono text-[10.5px] text-muted-foreground">
              <span className="text-foreground/80">{contrato.codigo}</span>
              {contrato.cliente_nombre ? <> · {contrato.cliente_nombre}</> : null}
            </p>
          </div>
          <span
            className={`ml-1 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${
              estatusColor[contrato.estatus] ?? "bg-muted text-muted-foreground"
            }`}
          >
            {contrato.estatus}
          </span>
        </div>

        {/* ── Cuerpo: rail · contenido · resumen ── */}
        <div className="grid grid-cols-1 md:grid-cols-[196px_minmax(0,1fr)] xl:grid-cols-[196px_minmax(0,1fr)_272px]">
          {/* Rail */}
          <nav className="flex gap-1 overflow-x-auto border-b p-2 md:block md:space-y-0.5 md:border-b-0 md:border-r">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = section === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSection(s.id)}
                  className={`relative flex w-auto flex-none items-start gap-2.5 rounded-lg p-2.5 text-left md:w-full ${
                    active ? "bg-primary/15" : "hover:bg-primary/5"
                  }`}
                >
                  <Icon size={15} className={`mt-0.5 flex-none ${active ? "text-primary" : "text-muted-foreground"}`} />
                  <span className="hidden min-w-0 md:block">
                    <span className={`block text-xs font-semibold ${active ? "" : "text-muted-foreground"}`}>
                      {s.title}
                    </span>
                    <span className="mt-0.5 block text-[10px] leading-snug text-muted-foreground/70">
                      {s.desc}
                    </span>
                  </span>
                  {panesChanged.has(s.id) && (
                    <span className="absolute right-2 top-2.5 h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Contenido */}
          <div className="max-h-[58vh] overflow-y-auto p-5 md:max-h-[62vh]">
            {/* ═══ SERVICIO ═══ */}
            <div className={section === "servicio" ? "" : "hidden"}>
              <div className="mb-3 flex items-center justify-between">
                <SectionKick>Tipo de servicio</SectionKick>
                <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {hasFiber ? <Zap size={11} /> : <Wifi size={11} />}
                  {hasFiber ? "Fibra" : "Wireless"}
                </span>
              </div>
              <p className="mb-4 text-[10.5px] leading-relaxed text-muted-foreground">
                Aquí solo se listan planes de {hasFiber ? "fibra" : "wireless"}. El cambio
                wireless ↔ fibra se hace con el switch de tecnología (migración: equipo nuevo,
                router/red, PPPoE y cargo), no cambiando el plan.
              </p>

              {canChangePlan && (
                <div className="mb-4">
                  <Label>Plan contratado</Label>
                  <Select {...register("plan_id")}>
                    {planes.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre} — ${Number(p.precio).toFixed(2)} /
                        {" "}{p.velocidad_down_mbps}↓/{p.velocidad_up_mbps}↑ Mbps
                      </option>
                    ))}
                  </Select>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Al cambiar el plan, el <strong>precio personalizado</strong> se elimina y
                    las próximas facturas usan el precio del plan nuevo. Las ya emitidas no
                    se tocan.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium">
                    <input
                      type="checkbox"
                      checked={useOverridePrecio}
                      onChange={(e) => togglePrecioOverride(e.target.checked)}
                    />
                    Precio distinto al del plan
                  </label>
                  <Input
                    type="number" step="0.01" min={0.01}
                    placeholder={useOverridePrecio ? "Ej. 300.00" : "Usa precio del plan"}
                    disabled={!useOverridePrecio}
                    className="mt-1"
                    {...register("precio_override")}
                  />
                  {errors.precio_override && useOverridePrecio && (
                    <span className="text-xs text-destructive">
                      {errors.precio_override.message as string}
                    </span>
                  )}
                </div>
              </div>

              {/* Equipo — detalle técnico */}
              {hasWireless && (
                <div className="mt-4 rounded-lg border bg-muted/20 p-3.5">
                  <SectionKick>Equipo · detalle wireless</SectionKick>
                  <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
                    Cambiar la MAC dispara re-aprovisionamiento y valida que no esté en uso.
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <Label>Vendor</Label>
                      <Select {...register("wireless_detalle.cpe_vendor")}>
                        <option value="">—</option>
                        {cpeVendorEnum.map((v) => (
                          <option key={v} value={v}>{CPE_VENDOR_LABEL[v]}</option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <Label>MAC CPE</Label>
                      <Input {...register("wireless_detalle.cpe_mac")} placeholder="AA:BB:CC:DD:EE:FF" />
                    </div>
                    <div>
                      <Label>Modelo</Label>
                      <Input {...register("wireless_detalle.cpe_modelo")} />
                    </div>
                    <div>
                      <Label>Serial</Label>
                      <Input {...register("wireless_detalle.cpe_serial")} />
                    </div>
                    <div>
                      <Label>IP mgmt</Label>
                      <Input {...register("wireless_detalle.cpe_ip_management")} placeholder="192.168.88.10" />
                    </div>
                    <div>
                      <Label>Polarización</Label>
                      <Select {...register("wireless_detalle.polarizacion")}>
                        <option value="">—</option>
                        {polarizacionEnum.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <Label>SSID</Label>
                      <Input {...register("wireless_detalle.ssid_conexion")} />
                    </div>
                    <div>
                      <Label>Frecuencia (MHz)</Label>
                      <Input type="number" {...register("wireless_detalle.frecuencia_mhz")} />
                    </div>
                    <div>
                      <Label>Usuario admin CPE</Label>
                      <Input {...register("wireless_detalle.cpe_usuario_admin")} />
                    </div>
                    <div>
                      <Label>Password admin CPE</Label>
                      <Input type="text" placeholder="Vacío = no cambiar"
                        {...register("wireless_detalle.cpe_password_admin")} />
                    </div>
                  </div>
                </div>
              )}
              {hasFiber && (
                <div className="mt-4 rounded-lg border bg-muted/20 p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <SectionKick>Equipo · detalle fibra</SectionKick>
                    <div className="flex gap-1.5">
                      {/* rc118 — mover de domicilio (misma serie, otro PON/OLT) */}
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setShowMoverOnu(true)}
                      >
                        Mover ONU…
                      </Button>
                      {/* rc117 — reemplazo de ONU (equipo quemado/dañado) */}
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setShowReemplazoOnu(true)}
                      >
                        Reemplazar ONU…
                      </Button>
                    </div>
                  </div>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    &quot;Mover ONU&quot;: cambio de domicilio — misma serie, se re-aprovisiona
                    en el PON/OLT nuevo (PPPoE y ACS intactos). &quot;Reemplazar ONU&quot;: equipo
                    quemado/cambiado — baja en ByteOLT/ByteACS, alta de la nueva y PPPoE
                    regenerado. Todo auditado.
                  </p>
                  <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
                    Cambiar la MAC dispara re-aprovisionamiento y valida que no esté en uso.
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <Label>Vendor ONU</Label>
                      <Select {...register("fiber_detalle.onu_vendor")}>
                        <option value="">—</option>
                        {onuVendorEnum.map((v) => (
                          <option key={v} value={v}>{ONU_VENDOR_LABEL[v]}</option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <Label>MAC ONU</Label>
                      <Input {...register("fiber_detalle.onu_mac")} />
                    </div>
                    <div>
                      <Label>Serial ONU</Label>
                      <Input {...register("fiber_detalle.onu_serial")} />
                    </div>
                    <div>
                      <Label>Modelo</Label>
                      <Input {...register("fiber_detalle.onu_modelo")} />
                    </div>
                    <div>
                      <Label>IP mgmt</Label>
                      <Input {...register("fiber_detalle.onu_ip_management")} />
                    </div>
                    <div>
                      <Label>Puerto OLT</Label>
                      <Input {...register("fiber_detalle.olt_puerto")} />
                    </div>
                    <div>
                      <Label>Splitter</Label>
                      <Select {...register("fiber_detalle.splitter_nivel")}>
                        <option value="">—</option>
                        {splitterNivelEnum.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* F92 — comodato vs propiedad */}
              <div className="mt-4 rounded-lg border bg-muted/20 p-3.5">
                <SectionKick>Equipo (CPE/router/antena)</SectionKick>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Modalidad</Label>
                    <Select {...register("equipo_modalidad")}>
                      <option value="comodato">Comodato (ISP recupera al cancelar)</option>
                      <option value="propiedad_cliente">Propiedad del cliente (no se recupera)</option>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Descripción</Label>
                    <Input
                      {...register("equipo_descripcion")}
                      placeholder="Ej. TP-Link Archer C20 + antena LBE-5AC-Gen2"
                    />
                  </div>
                  <div>
                    <Label>Costo instalación (MXN)</Label>
                    <Input type="number" step="0.01" min="0"
                      {...register("equipo_costo_instalacion")} placeholder="0" />
                  </div>
                </div>
              </div>
            </div>

            {/* ═══ CONEXIÓN ═══ */}
            <div className={section === "conexion" ? "" : "hidden"}>
              <SectionKick>Router y segmento</SectionKick>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <Label>Router</Label>
                  <Select value={routerId} onChange={(e) => onRouterChange(e.target.value)}>
                    {routers.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.nombre}{r.id === contrato.router_id ? " (actual)" : ""}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label>Segmento (red)</Label>
                  <Select value={redId} onChange={(e) => { setRedId(e.target.value); setIpSel(""); }}>
                    {routerId !== contrato.router_id && (
                      <option value="">
                        {redes.length ? "Selecciona segmento…" : "Este router no tiene segmentos"}
                      </option>
                    )}
                    {redes.map((rd) => (
                      <option key={rd.id} value={rd.id}>
                        {rd.nombre} · {rd.cidr}{rd.id === contrato.red_id ? " (actual)" : ""}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label>IP asignada</Label>
                {isMoving ? (
                  <Select disabled value="">
                    <option value="">auto — se toma del pool del segmento destino</option>
                  </Select>
                ) : (
                  <Select value={ipSel} onChange={(e) => setIpSel(e.target.value)} className="font-mono">
                    <option value="">conservar {ipActual}</option>
                    {ipsLoading && <option disabled>Cargando IPs libres…</option>}
                    {ipsLibres.map((x) => (
                      <option key={x} value={x}>{x} (libre)</option>
                    ))}
                  </Select>
                )}
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {isMoving
                    ? "Al mover de router la IP nueva se asigna automáticamente (regla rc82)."
                    : "Cambiarla recrea el secret (breve corte) y actualiza la IP de gestión del CPE."}
                </p>
              </div>

              {isMoving && (
                <div className="mt-3 flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs">
                  <span className="text-muted-foreground">Traslado:</span>
                  <span className="font-medium">{routerActual?.nombre ?? "—"}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{ipActual}</span>
                  <ArrowRight size={13} className="text-primary" />
                  <span className="font-medium text-primary">{routerDest?.nombre ?? "elige destino"}</span>
                  {redDestObj && (
                    <span className="font-mono text-[10px] text-muted-foreground">{redDestObj.cidr}</span>
                  )}
                </div>
              )}

              <div className="mt-5">
                <SectionKick>Credenciales PPPoE</SectionKick>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <Label>Usuario PPPoE</Label>
                  <Input {...register("pppoe_user")} placeholder={contrato.pppoe_user} />
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Actual: <span className="font-mono">{contrato.pppoe_user}</span> · vacío = conservar
                  </p>
                </div>
                <div>
                  <Label>Nueva password</Label>
                  <Input type="text" {...register("pppoe_password")}
                    placeholder="Dejar vacío = mantener la actual" />
                </div>
              </div>

              <div className="mt-4 flex items-start gap-2 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2.5 text-[11px] leading-relaxed text-primary">
                <Info size={13} className="mt-0.5 flex-none" />
                <span>
                  Todos los cambios de esta sección se aplican al router en{" "}
                  <strong>un solo reconcile</strong> al guardar — no un job por campo. En
                  traslados se retira del router anterior y se da de alta en el nuevo ya con
                  las credenciales finales.
                </span>
              </div>
            </div>

            {/* ═══ FACTURACIÓN ═══ */}
            <div className={section === "facturacion" ? "" : "hidden"}>
              <SectionKick>Cobro</SectionKick>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <Label>Día de corte</Label>
                  <Select {...register("fecha_corte", { valueAsNumber: true })}>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>Día {d} de cada mes</option>
                    ))}
                  </Select>
                  {errors.fecha_corte && (
                    <span className="text-xs text-destructive">{errors.fecha_corte.message}</span>
                  )}
                </div>
                <div>
                  <Label>Descuento %</Label>
                  <Input type="number" step="0.01" min={0} max={100} {...register("descuento_pct")} />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium">
                    <input
                      type="checkbox"
                      checked={useOverrideGracia}
                      onChange={(e) => toggleGraciaOverride(e.target.checked)}
                    />
                    Días de gracia distintos al default
                  </label>
                  <Input
                    type="number" min={0} max={60}
                    placeholder={useOverrideGracia ? "Ej. 3" : "Usa config global"}
                    disabled={!useOverrideGracia}
                    className="mt-1"
                    {...register("dias_gracia_override")}
                  />
                </div>
                <div>
                  <Label>Facturas vencidas permitidas</Label>
                  <Select {...register("max_facturas_vencidas_permitidas", { valueAsNumber: true })}>
                    <option value={0}>0 — Sin tolerancia (corte normal)</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n} — Tolera {n} factura{n > 1 ? "s" : ""} extra
                      </option>
                    ))}
                  </Select>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Solo cuenta <strong>mensualidades</strong>; N ≥ 1 = puede deber hasta N
                    facturas extra antes del corte automático. Requiere permiso admin.
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <SectionKick>Banderas</SectionKick>
              </div>
              <div className="mt-2 space-y-3">
                <label className="flex items-start gap-2 text-xs">
                  <input type="checkbox" className="mt-0.5" {...register("exento_facturacion")} />
                  <span>
                    Exento de facturación
                    <span className="block text-[10px] text-muted-foreground">
                      No se emiten mensualidades para este contrato.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs">
                  <input type="checkbox" className="mt-0.5" {...register("exento_monitoreo")} />
                  <span>
                    Exento de monitoreo
                    <span className="block text-[10px] text-muted-foreground">
                      ByteNMS no genera alertas de este servicio.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs">
                  <input type="checkbox" className="mt-0.5" {...register("facturar_suspendido")} />
                  <span>
                    Seguir facturando estando suspendido
                    <span className="block text-[10px] text-muted-foreground">
                      Apagado (default): al suspenderse, el poller deja de emitir mensualidades
                      hasta reactivarse. Emisión manual y cargos libres no se afectan.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* ═══ UBICACIÓN ═══ */}
            <div className={section === "ubicacion" ? "" : "hidden"}>
              <SectionKick>Ubicación de la instalación</SectionKick>
              <div className="mt-2">
                {section === "ubicacion" && (
                  <LocationPicker
                    value={hasGps ? { lat: latNum, lng: lngNum } : null}
                    onChange={(loc) => {
                      setValue("latitud", Number(loc.lat.toFixed(7)), { shouldValidate: true, shouldDirty: true });
                      setValue("longitud", Number(loc.lng.toFixed(7)), { shouldValidate: true, shouldDirty: true });
                    }}
                    heightPx={230}
                    tiles="hybrid"
                  />
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <Label>Latitud</Label>
                  <Input placeholder="20.2763990" {...register("latitud")} />
                </div>
                <div>
                  <Label>Longitud</Label>
                  <Input placeholder="-98.1501430" {...register("longitud")} />
                </div>
                <div className="col-span-2">
                  <Label>Referencia</Label>
                  <Input placeholder="Pahuatlán centro, casa azul…" {...register("ubicacion_referencia")} />
                </div>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">
                GPS de ESTE contrato (un cliente puede tener varios domicilios). Click para
                marcar, arrastra el pin para afinar. Vaciar latitud borra la ubicación completa.
              </p>
            </div>

            {/* ═══ NOTAS ═══ */}
            <div className={section === "notas" ? "" : "hidden"}>
              <SectionKick>Notas</SectionKick>
              <div className="mt-2 space-y-3">
                <div>
                  <Label>Notas</Label>
                  <Textarea rows={2} placeholder="Visibles en la ficha…" {...register("notas")} />
                </div>
                <div>
                  <Label>Notas internas</Label>
                  <Textarea rows={2} placeholder="Solo personal…" {...register("notas_internas")} />
                </div>
              </div>
              <div className="mt-4">
                <SectionKick>Banderas operativas</SectionKick>
              </div>
              <label className="mt-2 flex items-start gap-2 text-xs">
                <input type="checkbox" className="mt-0.5" {...register("excluir_reintento_auto")} />
                <span>
                  Excluir de reintentos masivos
                  <span className="block text-[10px] text-muted-foreground">
                    Para clientes que siempre fallan por razón conocida (antena muerta, visita
                    pendiente). El botón &quot;Reintentar errores&quot; lo omite; el reintento
                    individual sigue disponible.
                  </span>
                </span>
              </label>
            </div>

            {/* Errores */}
            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {isSubmitted && !isValid && Object.keys(errors).length > 0 && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">Revisa estos campos:</span>
                  <ul className="list-inside list-disc">
                    {flattenRhfErrors(errors as Record<string, unknown>)
                      .slice(0, 10)
                      .map(({ path, message }) => (
                        <li key={path}>
                          <span className="font-medium">{labelForPath(path)}</span>
                          {message && `: ${message}`}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* ── Panel resumen de cambios ── */}
          <aside className="flex flex-col border-t bg-muted/20 md:col-span-2 xl:col-span-1 xl:border-l xl:border-t-0">
            <div className="border-b px-4 py-3">
              <div className="flex items-center gap-2 text-xs font-semibold">
                Resumen de cambios
                <span className="rounded-full bg-primary/15 px-2 py-0.5 font-mono text-[10px] text-primary">
                  {cambios.length}
                </span>
              </div>
              <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
                Nada se aplica hasta que guardes. Un solo guardado, una sola auditoría.
              </p>
            </div>
            <div className="max-h-[170px] flex-1 overflow-y-auto p-2.5 xl:max-h-none">
              {cambios.length === 0 ? (
                <p className="px-2 py-6 text-center text-[11px] leading-relaxed text-muted-foreground">
                  Sin cambios todavía.<br />
                  Edita cualquier campo y aparecerá aquí con su antes → después.
                </p>
              ) : (
                cambios.map((c) => (
                  <div key={c.key} className="mb-2 rounded-lg border bg-background px-2.5 py-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                      {c.label}
                      {c.router && (
                        <span className="rounded bg-amber-500/15 px-1 py-px font-mono text-[8px] uppercase tracking-wider text-amber-500">
                          router
                        </span>
                      )}
                    </div>
                    <div className="mt-1 break-all font-mono text-[10px] leading-relaxed text-muted-foreground">
                      {showVal(c.before)}
                      <span className="px-1">→</span>
                      <span className="text-primary">{showVal(c.after)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            {routerN > 0 && (
              <div className="mx-2.5 mb-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[10.5px] leading-relaxed text-amber-600 dark:text-amber-400">
                <strong>⚠ {routerN} cambio{routerN > 1 ? "s" : ""} toca{routerN > 1 ? "n" : ""} el
                router</strong> — se aplica en {isMoving ? "un retiro + un alta" : "1 reconcile"}
                {" "}(breve corte posible).
              </div>
            )}
            <div className="border-t p-3">
              <Label className="text-[10.5px]">
                Motivo del cambio * <span className="text-muted-foreground">(auditoría)</span>
              </Label>
              <Input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Cliente cambió de domicilio a…"
                className="mt-1"
              />
              {motivo.trim().length > 0 && motivo.trim().length < 5 && (
                <p className="mt-1 text-[10px] text-destructive">Mínimo 5 caracteres.</p>
              )}
              <Button type="submit" size="sm" className="mt-2.5 w-full" disabled={!canSave}>
                {editarMut.isPending
                  ? "Guardando…"
                  : cambios.length > 0
                    ? `Guardar ${cambios.length} cambio${cambios.length > 1 ? "s" : ""}`
                    : "Guardar cambios"}
              </Button>
              <Button
                type="button" variant="ghost" size="sm" className="mt-1.5 w-full"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
            </div>
          </aside>
        </div>
      </form>
    </Dialog>

    {/* rc117 — Reemplazo de ONU: baja OLT+ACS de la actual, alta de la nueva
        en ambos y PPPoE regenerado de la serie nueva. Al terminar cierra el
        editor (el detalle se refresca solo por la invalidación del hook). */}
    {/* rc118 — Mover de domicilio (PON/OLT nuevos, misma serie) */}
    {hasFiber && showMoverOnu && (
      <MoverOnuDialog
        open={showMoverOnu}
        onOpenChange={setShowMoverOnu}
        contrato={contrato}
        onDone={() => {
          setShowMoverOnu(false);
          onOpenChange(false);
          onSaved?.();
        }}
      />
    )}
    {hasFiber && showReemplazoOnu && (
      <ReemplazarOnuDialog
        open={showReemplazoOnu}
        onOpenChange={setShowReemplazoOnu}
        contrato={contrato}
        onDone={() => {
          setShowReemplazoOnu(false);
          onOpenChange(false);
          onSaved?.();
        }}
      />
    )}
    </>
  );
}

function SectionKick({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-muted-foreground">
      {children}
    </div>
  );
}

/** Construye defaults desde el contrato para llenar el form al abrir. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildDefaults(c: ContratoDetail): any {
  return {
    plan_id: c.plan_id,
    fecha_corte: c.fecha_corte,
    // precio_override === 0 en DB se considera "sin override" — lo mostramos
    // vacío para que el user no se confunda. Migración 012 limpia existentes.
    precio_override:
      c.precio_override === null || c.precio_override === undefined ||
      Number(c.precio_override) === 0
        ? ""
        : c.precio_override,
    descuento_pct: Number(c.descuento_pct ?? 0),
    dias_gracia_override: c.dias_gracia_override ?? "",
    exento_facturacion: c.exento_facturacion,
    exento_monitoreo: c.exento_monitoreo,
    // rc90 — facturación automática estando suspendido (default false)
    facturar_suspendido: c.facturar_suspendido ?? false,
    // rc97 — excluido del reintento masivo (default false)
    excluir_reintento_auto: c.excluir_reintento_auto ?? false,
    // rc101 — ubicación GPS
    latitud: c.latitud ?? "",
    longitud: c.longitud ?? "",
    ubicacion_referencia: c.ubicacion_referencia ?? "",
    // rc44 — tolerancia facturas vencidas (default 0)
    max_facturas_vencidas_permitidas: c.max_facturas_vencidas_permitidas ?? 0,
    // F92 — equipo
    equipo_modalidad: c.equipo_modalidad ?? "comodato",
    equipo_descripcion: c.equipo_descripcion ?? "",
    equipo_costo_instalacion: Number(c.equipo_costo_instalacion ?? 0),
    notas: c.notas ?? "",
    notas_internas: c.notas_internas ?? "",
    tags: c.tags ?? [],
    pppoe_user: "",
    pppoe_password: "",
    wireless_detalle: c.wireless_detalle
      ? {
          cpe_vendor: c.wireless_detalle.cpe_vendor ?? "",
          cpe_modelo: c.wireless_detalle.cpe_modelo ?? "",
          cpe_mac: c.wireless_detalle.cpe_mac ?? "",
          cpe_serial: c.wireless_detalle.cpe_serial ?? "",
          cpe_ip_management: c.wireless_detalle.cpe_ip_management ?? "",
          cpe_usuario_admin: c.wireless_detalle.cpe_usuario_admin ?? "",
          cpe_password_admin: "",
          ssid_conexion: c.wireless_detalle.ssid_conexion ?? "",
          frecuencia_mhz: c.wireless_detalle.frecuencia_mhz ?? "",
          polarizacion: c.wireless_detalle.polarizacion ?? "",
          sitio_id: c.wireless_detalle.sitio_id ?? "",
          notas_instalacion: c.wireless_detalle.notas_instalacion ?? "",
        }
      : undefined,
    fiber_detalle: c.fiber_detalle
      ? {
          onu_vendor: c.fiber_detalle.onu_vendor ?? "",
          onu_modelo: c.fiber_detalle.onu_modelo ?? "",
          onu_serial: c.fiber_detalle.onu_serial ?? "",
          onu_mac: c.fiber_detalle.onu_mac ?? "",
          onu_ip_management: c.fiber_detalle.onu_ip_management ?? "",
          onu_usuario_admin: c.fiber_detalle.onu_usuario_admin ?? "",
          onu_password_admin: "",
          olt_puerto: c.fiber_detalle.olt_puerto ?? "",
          splitter_nivel: c.fiber_detalle.splitter_nivel ?? "",
          potencia_optica_dbm_esperada:
            c.fiber_detalle.potencia_optica_dbm_esperada ?? "",
          distancia_m_estimada: c.fiber_detalle.distancia_m_estimada ?? "",
          sitio_olt_id: c.fiber_detalle.sitio_olt_id ?? "",
          notas_instalacion: c.fiber_detalle.notas_instalacion ?? "",
        }
      : undefined,
  };
}

/** Quita strings vacíos antes del submit. */
function clean<T>(v: T): T {
  if (typeof v === "string") return (v.trim() === "" ? undefined : v.trim()) as T;
  if (Array.isArray(v)) return v.map(clean) as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      const c = clean(val);
      if (c !== undefined) out[k] = c;
    }
    return Object.keys(out).length > 0 ? (out as T) : (undefined as T);
  }
  return v;
}
